import React, {
  useCallback,
  useImperativeHandle,
  forwardRef,
  useMemo
} from "react";
import {
  createHandleChange,
  validateInvoiceFormForKind,
  isFieldRequiredForKind,
  submitInvoiceForm
} from "../../../../../../../../lib/invoiceFormValidators/invoiceFormValidators";
import InputText from "../../../../../../common/inputs/InputText/InputText";
import SelectInput from "../../../../../../common/inputs/SelectInput/SelectInput";
import {
  BUSINESS_ACTIVITY_KIND,
  DEFAULT_INVOICE_COUNTRY,
  FORM_DATA_KEY_BY_KIND,
  INVOICE_DATA_FIELDS
} from "../../../../../../../../redux/modules/invoices/invoiceFields";
import useInvoices from "../../../../../../../../hooks/useInvoices";
import useFetchGus from "../../../../../../../../hooks/useFetchGus";
import { fieldNameToFieldLabeld } from "../../../../../../../../redux/modules/invoices/dto/updateSavedInvoiceDataErrorsToFormErrors";

const KIND = BUSINESS_ACTIVITY_KIND.SELF_EMPLOYED;
const FORM_DATA_KEY = FORM_DATA_KEY_BY_KIND[KIND];

const SelfEmployedInvoiceForm = forwardRef(
  ({ translate, locale, initialFormData = {} }, ref) => {
    const {
      updateInvoiceFormData,
      invoiceFormData,
      invoiceFormErrors,
      invoiceCountries,
      updateInvoiceFormError
    } = useInvoices();

    const formData = useMemo(() => {
      return {
        [INVOICE_DATA_FIELDS.COUNTRY]: DEFAULT_INVOICE_COUNTRY,
        ...initialFormData,
        ...invoiceFormData[FORM_DATA_KEY]
      };
    }, [invoiceFormData, initialFormData]);

    const errors = useMemo(() => {
      return invoiceFormErrors?.[FORM_DATA_KEY] ?? {};
    }, [invoiceFormErrors]);

    const countryOptions = useMemo(
      () =>
        (invoiceCountries ?? []).map(({ name, value }) => ({
          label: name,
          value
        })),
      [invoiceCountries]
    );

    const selectedCountryOption = useMemo(() => {
      const countryValue = formData[INVOICE_DATA_FIELDS.COUNTRY];
      if (!countryValue) {
        return null;
      }
      return (
        countryOptions.find(option => option.value === countryValue) ?? {
          label: countryValue,
          value: countryValue
        }
      );
    }, [countryOptions, formData]);

    const { fetchGus, loadingGus } = useFetchGus({
      locale,
      nip: formData[INVOICE_DATA_FIELDS.NIP],
      formDataKey: FORM_DATA_KEY,
      updateInvoiceFormData,
      updateInvoiceFormError,
      invoiceFormErrors,
      translate
    });

    const handleChange = useMemo(
      () =>
        createHandleChange({
          formData,
          errors,
          updateInvoiceFormData,
          updateInvoiceFormError,
          formDataKey: FORM_DATA_KEY
        }),
      [formData, errors, updateInvoiceFormData, updateInvoiceFormError]
    );

    const validate = useCallback(
      () => validateInvoiceFormForKind(KIND, formData, translate),
      [formData, translate]
    );

    const handleSubmit = useCallback(
      () =>
        submitInvoiceForm({
          validate,
          formData,
          updateInvoiceFormError,
          formDataKey: FORM_DATA_KEY
        }),
      [formData, validate, updateInvoiceFormError]
    );

    const getFieldLabel = useCallback(
      field => translate(fieldNameToFieldLabeld[field]),
      [translate]
    );

    const getIsRequired = useCallback(
      field => isFieldRequiredForKind(KIND, field),
      []
    );

    useImperativeHandle(
      ref,
      () => ({
        submit: handleSubmit
      }),
      [handleSubmit]
    );

    return (
      <>
        <InputText
          name={INVOICE_DATA_FIELDS.NIP}
          label={getFieldLabel(INVOICE_DATA_FIELDS.NIP)}
          isRequired={getIsRequired(INVOICE_DATA_FIELDS.NIP)}
          value={formData[INVOICE_DATA_FIELDS.NIP]}
          onChange={handleChange(INVOICE_DATA_FIELDS.NIP)}
          errorMessage={errors[INVOICE_DATA_FIELDS.NIP]}
          onButtonClick={fetchGus}
          disableButton={loadingGus}
          buttonText={translate("invoice-form_gus")}
        />
        <InputText
          name={INVOICE_DATA_FIELDS.COMPANY_NAME}
          label={getFieldLabel(INVOICE_DATA_FIELDS.COMPANY_NAME)}
          isRequired={getIsRequired(INVOICE_DATA_FIELDS.COMPANY_NAME)}
          value={formData[INVOICE_DATA_FIELDS.COMPANY_NAME]}
          onChange={handleChange(INVOICE_DATA_FIELDS.COMPANY_NAME)}
          errorMessage={errors[INVOICE_DATA_FIELDS.COMPANY_NAME]}
        />
        <InputText
          name={INVOICE_DATA_FIELDS.FIRST_NAME}
          label={getFieldLabel(INVOICE_DATA_FIELDS.FIRST_NAME)}
          isRequired={getIsRequired(INVOICE_DATA_FIELDS.FIRST_NAME)}
          value={formData[INVOICE_DATA_FIELDS.FIRST_NAME]}
          onChange={handleChange(INVOICE_DATA_FIELDS.FIRST_NAME)}
          errorMessage={errors[INVOICE_DATA_FIELDS.FIRST_NAME]}
        />
        <InputText
          name={INVOICE_DATA_FIELDS.LAST_NAME}
          label={getFieldLabel(INVOICE_DATA_FIELDS.LAST_NAME)}
          isRequired={getIsRequired(INVOICE_DATA_FIELDS.LAST_NAME)}
          value={formData[INVOICE_DATA_FIELDS.LAST_NAME]}
          onChange={handleChange(INVOICE_DATA_FIELDS.LAST_NAME)}
          errorMessage={errors[INVOICE_DATA_FIELDS.LAST_NAME]}
        />
        <InputText
          name={INVOICE_DATA_FIELDS.STREET}
          label={getFieldLabel(INVOICE_DATA_FIELDS.STREET)}
          isRequired={getIsRequired(INVOICE_DATA_FIELDS.STREET)}
          value={formData[INVOICE_DATA_FIELDS.STREET]}
          onChange={handleChange(INVOICE_DATA_FIELDS.STREET)}
          errorMessage={errors[INVOICE_DATA_FIELDS.STREET]}
        />
        <InputText
          name={INVOICE_DATA_FIELDS.STREET_NUMBER}
          label={getFieldLabel(INVOICE_DATA_FIELDS.STREET_NUMBER)}
          isRequired={getIsRequired(INVOICE_DATA_FIELDS.STREET_NUMBER)}
          value={formData[INVOICE_DATA_FIELDS.STREET_NUMBER]}
          onChange={handleChange(INVOICE_DATA_FIELDS.STREET_NUMBER)}
          errorMessage={errors[INVOICE_DATA_FIELDS.STREET_NUMBER]}
        />
        <InputText
          name={INVOICE_DATA_FIELDS.FLAT_NUMBER}
          label={getFieldLabel(INVOICE_DATA_FIELDS.FLAT_NUMBER)}
          isRequired={getIsRequired(INVOICE_DATA_FIELDS.FLAT_NUMBER)}
          value={formData[INVOICE_DATA_FIELDS.FLAT_NUMBER]}
          onChange={handleChange(INVOICE_DATA_FIELDS.FLAT_NUMBER)}
          errorMessage={errors[INVOICE_DATA_FIELDS.FLAT_NUMBER]}
        />
        <InputText
          name={INVOICE_DATA_FIELDS.POSTAL_CODE}
          label={getFieldLabel(INVOICE_DATA_FIELDS.POSTAL_CODE)}
          isRequired={getIsRequired(INVOICE_DATA_FIELDS.POSTAL_CODE)}
          value={formData[INVOICE_DATA_FIELDS.POSTAL_CODE]}
          onChange={handleChange(INVOICE_DATA_FIELDS.POSTAL_CODE)}
          errorMessage={errors[INVOICE_DATA_FIELDS.POSTAL_CODE]}
        />
        <InputText
          name={INVOICE_DATA_FIELDS.CITY}
          label={getFieldLabel(INVOICE_DATA_FIELDS.CITY)}
          isRequired={getIsRequired(INVOICE_DATA_FIELDS.CITY)}
          value={formData[INVOICE_DATA_FIELDS.CITY]}
          onChange={handleChange(INVOICE_DATA_FIELDS.CITY)}
          errorMessage={errors[INVOICE_DATA_FIELDS.CITY]}
        />
        <div>
          <SelectInput
            label={getFieldLabel(INVOICE_DATA_FIELDS.COUNTRY)}
            isRequired={getIsRequired(INVOICE_DATA_FIELDS.COUNTRY)}
            options={countryOptions}
            isMulti={false}
            value={selectedCountryOption ? [selectedCountryOption] : []}
            onChange={selected =>
              handleChange(INVOICE_DATA_FIELDS.COUNTRY)(
                selected[0]?.value ?? ""
              )
            }
          />
          {errors[INVOICE_DATA_FIELDS.COUNTRY] ? (
            <span className="text-red text-xs mt-1 ml-1 block">
              {errors[INVOICE_DATA_FIELDS.COUNTRY]}
            </span>
          ) : null}
        </div>
      </>
    );
  }
);

export default SelfEmployedInvoiceForm;
